import { useQuery } from '@apollo/client';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Card } from '@ovComponents/2-component/card/card';
import { UserContext } from '@providers/userContextProvider';
import { Box, Button, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { startTransition, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { FETCH_DASHBOARD_STATS } from '../queries';
import {
  BOLD_SX,
  CARD_HEADER_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SMALL_SX,
  TRUNCATE_SX,
  VALUE_SX,
} from '../styles';
import { useTradingHub } from '../tradingHubContext';
import type { BulkTradeRunState, OptimizerState } from '../types';
import {
  BULK_TRADE_RUN_STATE_LABELS,
  getBulkTradeRunProgress,
  getOptimizerProgress,
  getPortfolioName,
  OPTIMIZER_STATE_LABELS,
} from '../utils';
import { translateBatchRecap } from '../utils/batchRecapI18n';
import { deriveDashboardStats } from '../utils/dashboardStats';
import { formatRelativeTime } from '../utils/relativeTimeI18n';
import { mapDashboardToBatchRecap } from '../utils/v2Mappers';
import OperationRow from './operationRow';
import PendingCashFlows from './pendingCashFlows';

const TodaysActivityTab = () => {
  const formatMoney = useFormatMoney();
  const { t } = useTranslation(['portfolioTradingHub', 'batchRecap']);
  const theme = useTheme();
  const { activeOrganization } = useContext(UserContext);
  const {
    updateStatusBarStats,
    setCurrentTab,
    setPortfolioStatusPreFilter,
    setPinnedPortfolioDeepLink,
    refreshKey,
  } = useTradingHub();

  const {
    loading: loadingStats,
    data: statsData,
    refetch: refetchStats,
  } = useQuery(FETCH_DASHBOARD_STATS, {
    variables: { organizationId: activeOrganization?.id },
    skip: !activeOrganization?.id,
  });

  useEffect(() => {
    if (refreshKey > 0) refetchStats();
  }, [refreshKey]);

  const dashboardStats = useMemo(
    () => deriveDashboardStats(statsData),
    [statsData],
  );
  const {
    portfolioStats,
    actionQueue,
    operationsStats,
    tradeStats,
    optimizers,
    bulkRuns,
  } = dashboardStats;

  const batchRecap = useMemo(
    () =>
      translateBatchRecap(
        mapDashboardToBatchRecap(optimizers, bulkRuns),
        t,
        (dateStr) => formatRelativeTime(dateStr, t),
      ),
    [optimizers, bulkRuns, t],
  );

  useEffect(() => {
    if (statsData) {
      updateStatusBarStats({
        needsAction: portfolioStats.actionQueue,
        inProgress: operationsStats.totalActive,
        onTrack: portfolioStats.onTrack,
        totalAum: portfolioStats.totalValue,
        activeOperations: operationsStats.totalActive,
        tradesInFlight: tradeStats.total,
      });
    }
  }, [portfolioStats, operationsStats, tradeStats, statsData]);

  const summaryCards = useMemo(
    () => [
      {
        label: 'Needs Action',
        count: portfolioStats.actionQueue,
        accentColor:
          portfolioStats.actionQueue > 0
            ? theme.color.negative
            : theme.color.outlineVariant,
        extra: (
          <Typography
            variant='labelSmall'
            sx={{ color: theme.color.positive, fontWeight: 600 }}
          >
            {portfolioStats.onTrack} on track
          </Typography>
        ),
        subtitle: `${portfolioStats.urgent > 0 ? `${portfolioStats.urgent} action required · ` : ''}${portfolioStats.moderate > 0 ? `${portfolioStats.moderate} review · ` : ''}${formatMoney(portfolioStats.actionQueueValue, { compact: true })} AUM`,
        onClick: () => startTransition(() => setCurrentTab('portfolios')),
      },
      {
        label: 'Ops In Flight',
        count: operationsStats.totalActive,
        accentColor:
          operationsStats.totalActive > 0
            ? theme.color.warning
            : theme.color.outlineVariant,
        extra: (
          <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
            operations
          </Typography>
        ),
        subtitle: `${operationsStats.activeOptimizers.length} rebalances · ${operationsStats.activeBulkRuns.length} batches`,
        onClick: () => setCurrentTab('operations'),
      },
      {
        label: 'Trade Pipeline',
        count: tradeStats.total,
        accentColor:
          tradeStats.total > 0
            ? theme.color.primary
            : theme.color.outlineVariant,
        extra: (
          <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
            pending trades
          </Typography>
        ),
        subtitle: `${tradeStats.initiated} staged · ${tradeStats.ready} approved · ${tradeStats.requested} sent`,
        onClick: () => setCurrentTab('tradeRequests'),
      },
    ],
    [
      portfolioStats,
      operationsStats,
      tradeStats,
      setCurrentTab,
      theme,
      formatMoney,
    ],
  );

  const tradeFlowSegments = useMemo(
    () => [
      {
        key: 'initiated',
        count: tradeStats.initiated,
        color: theme.color.onSurfaceVariant,
      },
      { key: 'ready', count: tradeStats.ready, color: theme.color.positive },
      {
        key: 'requested',
        count: tradeStats.requested,
        color: theme.color.primary,
      },
    ],
    [tradeStats, theme],
  );

  if (loadingStats && !statsData)
    return (
      <Box
        sx={{
          minHeight: '400px',
        }}
      >
        <Skeleton variant='text' width='40%' height={32} sx={{ mb: 0.5 }} />
        <Skeleton variant='text' width='25%' height={16} sx={{ mb: 3 }} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant='rectangular'
              sx={{ flex: 1, height: 80, borderRadius: 1 }}
            />
          ))}
        </Box>
        <Skeleton
          variant='rectangular'
          height={200}
          sx={{ borderRadius: 1, mb: 2 }}
        />
        <Skeleton variant='rectangular' height={120} sx={{ borderRadius: 1 }} />
      </Box>
    );

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <Typography variant='headingSmall' sx={{ mb: 0.25 }}>
          {t('todaysDashboard.title')}
        </Typography>
        <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
          {' • '}
          {portfolioStats.total} portfolios
          {' • '}
          {formatMoney(portfolioStats.totalValue, { compact: true })} AUM
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {summaryCards.map((card) => (
          <Card
            key={card.label}
            sx={{
              cursor: 'pointer',
              borderLeft: '3px solid',
              borderLeftColor: card.accentColor,
              '&:hover': { bgcolor: theme.color.neutralHover },
            }}
            onClick={card.onClick}
          >
            <Box
              sx={{
                p: 1.5,
              }}
            >
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...SECTION_HEADER_SX, mb: 0.25 }}
              >
                {card.label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                }}
              >
                <Typography
                  variant='headingMedium'
                  sx={{
                    color:
                      card.count > 0 ? card.accentColor : theme.color.onSurface,
                    ...BOLD_SX,
                  }}
                >
                  {card.count}
                </Typography>
                {card.extra}
              </Box>
              <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                {card.subtitle}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 0.8fr',
          gap: 2,
          mb: 2,
        }}
      >
        <Card>
          <Box
            sx={[
              {
                px: 2,
                py: 1,
              },
              ...(Array.isArray(CARD_HEADER_SX)
                ? CARD_HEADER_SX
                : [CARD_HEADER_SX]),
            ]}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant='labelSmall' sx={BOLD_SX}>
                Action Queue
              </Typography>
              {actionQueue.urgent.length + actionQueue.moderate.length > 0 && (
                <Badge
                  label={`${actionQueue.urgent.length + actionQueue.moderate.length}`}
                  color='negative'
                />
              )}
            </Box>
            <Button
              variant='text'
              size='small'
              onClick={() =>
                startTransition(() => {
                  setPortfolioStatusPreFilter('ANY_REBALANCE');
                  setCurrentTab('portfolios');
                })
              }
            >
              View All
            </Button>
          </Box>

          {loadingStats && !statsData ? (
            <Box
              sx={{
                py: 2,
              }}
            >
              {[0, 1, 2].map((i) => (
                <Skeleton
                  key={i}
                  variant='rectangular'
                  height={40}
                  sx={{ mb: 1, borderRadius: 0.5 }}
                />
              ))}
            </Box>
          ) : actionQueue.urgent.length + actionQueue.moderate.length > 0 ? (
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 80px 80px 20px',
                  gap: 0,
                  px: 2,
                  py: 0.5,
                  alignItems: 'center',
                  borderBottom: '1px solid',
                  borderColor: theme.color.outlineVariant,
                  position: 'sticky',
                  top: 0,
                  bgcolor: theme.color.background,
                  zIndex: 1,
                }}
              >
                {['PRI', 'PORTFOLIO', 'VALUE', 'REASON', ''].map((h) => (
                  <Typography
                    key={h || '_'}
                    variant='labelSmall'
                    sx={{ ...SECTION_HEADER_SX }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              {[...actionQueue.urgent, ...actionQueue.moderate]
                .slice(0, 25)
                .map((portfolio) => {
                  const isUrgent =
                    portfolio.liquidateRebalanceRequired ||
                    portfolio.transferRebalanceRequired;
                  const unverified = portfolio.unverifiedSubTradeRequests || 0;
                  const pending = portfolio.pendingSubTradeRequests || 0;
                  const needsVerify = unverified > 0;
                  let reason: string;
                  if (portfolio.liquidateRebalanceRequired)
                    reason = 'Liquidation';
                  else if (portfolio.transferRebalanceRequired)
                    reason = 'Transfer';
                  else if (needsVerify) reason = `${unverified} Unverified`;
                  else if (pending > 0) reason = `${pending} Pending`;
                  else reason = 'Drift';
                  let badgeLabel: string;
                  let badgeColor: 'negative' | 'warning' | 'neutral';
                  if (isUrgent) {
                    badgeLabel = 'ACTION';
                    badgeColor = 'negative';
                  } else if (needsVerify) {
                    badgeLabel = 'VERIFY';
                    badgeColor = 'warning';
                  } else {
                    badgeLabel = 'REVIEW';
                    badgeColor = 'neutral';
                  }
                  const borderColor = isUrgent
                    ? theme.color.negative
                    : needsVerify
                      ? theme.color.warning
                      : theme.color.onSurfaceVariant;
                  const acctType = portfolio.object?.account?.type;

                  return (
                    <Box
                      key={portfolio.id}
                      onClick={() => {
                        setPinnedPortfolioDeepLink({
                          portfolioReportId: portfolio.id,
                          report: portfolio,
                        });
                        setCurrentTab('portfolios');
                      }}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '60px 1fr 80px 80px 20px',
                        gap: 0,
                        px: 2,
                        py: 0.5,
                        alignItems: 'center',
                        borderBottom: '1px solid',
                        borderColor: theme.color.outlineVariant,
                        borderLeft: '3px solid',
                        borderLeftColor: borderColor,
                        cursor: 'pointer',
                        transition: 'background 0.12s ease',
                        '&:hover': { bgcolor: theme.color.neutralHover },
                        '&:hover .nav-arrow': { opacity: 1 },
                      }}
                    >
                      <Badge label={badgeLabel} color={badgeColor} />
                      <Box
                        sx={{
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant='labelSmall'
                          sx={{
                            fontWeight: 600,
                            ...TRUNCATE_SX,
                            lineHeight: 1.3,
                          }}
                        >
                          {getPortfolioName(portfolio)}
                        </Typography>
                        {acctType && (
                          <Typography
                            variant='labelSmall'
                            component='div'
                            sx={{
                              ...SMALL_SX,
                              color: theme.color.onDisabled,
                              lineHeight: 1,
                            }}
                          >
                            {acctType}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant='labelSmall' sx={BOLD_SX}>
                        {formatMoney(portfolio.marketValueCents, {
                          compact: true,
                        })}
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...BOLD_SX,
                          color: isUrgent
                            ? theme.color.negative
                            : needsVerify
                              ? theme.color.warning
                              : theme.color.onSurfaceVariant,
                        }}
                      >
                        {reason}
                      </Typography>
                      <Typography
                        className='nav-arrow'
                        variant='labelSmall'
                        sx={{
                          ...VALUE_SX,
                          color: theme.color.onDisabled,
                          opacity: 0,
                          transition: 'opacity 0.12s',
                        }}
                      >
                        →
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          ) : (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 28, color: theme.color.positive, mb: 0.5 }}
              />
              <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
                All portfolios on track
              </Typography>
            </Box>
          )}
        </Card>

        <Card>
          <Box
            sx={[
              {
                px: 2,
                py: 1,
              },
              ...(Array.isArray(CARD_HEADER_SX)
                ? CARD_HEADER_SX
                : [CARD_HEADER_SX]),
            ]}
          >
            <Typography variant='labelSmall' sx={BOLD_SX}>
              Pipeline
            </Typography>
            <Button
              variant='text'
              size='small'
              onClick={() => setCurrentTab('operations')}
            >
              View Ops
            </Button>
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: theme.color.outlineVariant,
            }}
          >
            <Typography
              variant='labelSmall'
              component='div'
              sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
            >
              TRADE FLOW
            </Typography>
            {tradeStats.total > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    height: 20,
                    borderRadius: '3px',
                    overflow: 'hidden',
                    mb: 0.75,
                  }}
                >
                  {tradeFlowSegments.map(
                    (seg) =>
                      seg.count > 0 && (
                        <Box
                          key={seg.key}
                          sx={{
                            flex: seg.count,
                            bgcolor: seg.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': { filter: 'brightness(0.9)' },
                          }}
                          onClick={() => setCurrentTab('tradeRequests')}
                        >
                          <Typography
                            variant='labelSmall'
                            sx={{ ...BOLD_SX, color: theme.color.onPrimary }}
                          >
                            {seg.count}
                          </Typography>
                        </Box>
                      ),
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                  }}
                >
                  {[
                    {
                      label: 'Staged',
                      count: tradeStats.initiated,
                      color: theme.color.onSurfaceVariant,
                    },
                    {
                      label: 'Approved',
                      count: tradeStats.ready,
                      color: theme.color.positive,
                    },
                    {
                      label: 'Sent',
                      count: tradeStats.requested,
                      color: theme.color.primary,
                    },
                  ].map((s) => (
                    <Box
                      key={s.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '2px',
                          bgcolor: s.color,
                        }}
                      />
                      <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                        {s.label} ({s.count})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...SECONDARY_TEXT_SX, py: 1 }}
              >
                No active trades
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1,
            }}
          >
            <Typography
              variant='labelSmall'
              component='div'
              sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
            >
              ACTIVE OPERATIONS ({operationsStats.totalActive})
            </Typography>
            {operationsStats.totalActive > 0 ? (
              <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                {operationsStats.activeOptimizers.map((op) => (
                  <OperationRow
                    key={op.id}
                    badgeLabel='OPT'
                    badgeColor='warning'
                    title={`${op.numPortfolios} portfolios`}
                    progress={getOptimizerProgress(op.state as OptimizerState)}
                    stateLabel={
                      OPTIMIZER_STATE_LABELS[op.state as OptimizerState] ||
                      op.state
                    }
                    elapsed={Math.round(
                      (Date.now() - new Date(op.createdAt).getTime()) / 60000,
                    )}
                    accentColor={theme.color.warning}
                  />
                ))}
                {operationsStats.activeBulkRuns.map((run) => (
                  <OperationRow
                    key={run.id}
                    badgeLabel='BATCH'
                    badgeColor='neutral'
                    title='Bulk Trade Run'
                    progress={getBulkTradeRunProgress(
                      run.state as BulkTradeRunState,
                    )}
                    stateLabel={
                      BULK_TRADE_RUN_STATE_LABELS[
                        run.state as BulkTradeRunState
                      ] || run.state
                    }
                    elapsed={Math.round(
                      (Date.now() - new Date(run.createdAt).getTime()) / 60000,
                    )}
                    accentColor={theme.color.primary}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  minHeight: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{ color: theme.color.positive, ...BOLD_SX, mb: 0.25 }}
                >
                  No active operations
                </Typography>
                <Typography
                  variant='labelSmall'
                  sx={{ ...SECONDARY_TEXT_SX, fontSize: '0.65rem' }}
                >
                  Rebalances and trade batches will appear here when in flight
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      <Card sx={{ mb: 2, overflow: 'hidden' }}>
        <Box
          sx={[
            {
              px: 2,
              py: 1,
            },
            ...(Array.isArray(CARD_HEADER_SX)
              ? CARD_HEADER_SX
              : [CARD_HEADER_SX]),
          ]}
        >
          <Typography variant='labelSmall' sx={BOLD_SX}>
            Cash Activity
          </Typography>
        </Box>
        <PendingCashFlows compact />
      </Card>

      <Card sx={{ overflow: 'hidden' }}>
        <Box
          sx={[
            {
              px: 2,
              py: 1,
            },
            ...(Array.isArray(CARD_HEADER_SX)
              ? CARD_HEADER_SX
              : [CARD_HEADER_SX]),
          ]}
        >
          <Typography variant='labelSmall' sx={BOLD_SX}>
            Yesterday's Recap — Last 48 Hours
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 0,
            borderBottom: '1px solid',
            borderColor: theme.color.outlineVariant,
          }}
        >
          {[
            {
              label: 'Batches Completed',
              value: `${batchRecap.summary.batchesRun.value}`,
              color:
                batchRecap.summary.batchesRun.value > 0
                  ? theme.color.positive
                  : undefined,
            },
            {
              label: 'Trades Sent',
              value: `${batchRecap.summary.tradesSent.value}`,
              color:
                batchRecap.summary.tradesSent.value > 0
                  ? theme.color.positive
                  : undefined,
            },
            ...(batchRecap.summary.failedOrCanceled.value > 0
              ? [
                  {
                    label: 'Failed / Canceled',
                    value: `${batchRecap.summary.failedOrCanceled.value}`,
                    color: theme.color.negative,
                  },
                ]
              : []),
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                flex: 1,
                py: 0.5,
                px: 1.5,
                borderRight: '1px solid',
                borderColor: theme.color.outlineVariant,
                '&:last-child': { borderRight: 'none' },
                bgcolor: theme.color.background,
              }}
            >
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...SECTION_HEADER_SX }}
              >
                {stat.label}
              </Typography>
              <Typography
                variant='bodySmall'
                component='div'
                sx={{ ...BOLD_SX, color: stat.color || theme.color.onSurface }}
              >
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {batchRecap.events.length > 0 ? (
          <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {batchRecap.events.map((event) => {
              const isCompleted = event.status === 'COMPLETED';
              return (
                <Box
                  key={event.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 0.5,
                    borderBottom: '1px solid',
                    borderColor: theme.color.outlineVariant,
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: theme.color.neutralHover },
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isCompleted
                        ? theme.color.positive
                        : theme.color.negative,
                      flexShrink: 0,
                    }}
                  />
                  <Badge
                    label={event.eventType}
                    color={event.eventType === 'OPT' ? 'supportOne' : 'neutral'}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      variant='labelSmall'
                      sx={{ fontWeight: 600, ...TRUNCATE_SX }}
                    >
                      {event.description}
                    </Typography>
                  </Box>
                  <Badge
                    label={event.status}
                    color={isCompleted ? 'positive' : 'negative'}
                  />
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...SECONDARY_TEXT_SX,
                      minWidth: 50,
                      textAlign: 'right',
                    }}
                  >
                    {event.timeElapsedLabel}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
              No operations completed in the last 48 hours
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default TodaysActivityTab;
